import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard components
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL and Cache
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes and context
import routes from "routes";
import { topLevelNav } from "config/sidenavConfig";
import ProtectedRoute from "security/ProtectedRoute";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, darkMode } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Hide the app-level sidenav on auth, on the full-page form builder, and on
  // website-detail pages (which render their own context sidenav). The
  // `/websites` listing keeps the app sidenav.
  const hideSidenav =
    pathname.startsWith("/authentication") ||
    pathname.includes("form-builder") ||
    /^\/websites\/[^/]+/.test(pathname);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);

      // Nested route: a parent path with child routes (an optional `index`
      // child plus path children). The parent's `protected` guard wraps all.
      if (route.children) {
        const element = route.protected ? (
          <ProtectedRoute>{route.component}</ProtectedRoute>
        ) : (
          route.component
        );

        return (
          <Route path={route.route} element={element} key={route.key}>
            {route.children.map((child) =>
              child.index ? (
                <Route index element={child.component} key={child.key} />
              ) : (
                <Route path={child.path} element={child.component} key={child.key} />
              )
            )}
          </Route>
        );
      }

      if (route.route) {
        const element = route.protected ? (
          <ProtectedRoute>{route.component}</ProtectedRoute>
        ) : (
          route.component
        );

        return <Route path={route.route} element={element} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const themeToUse = () => {
    if (direction === "rtl") {
      return darkMode ? themeDarkRTL : themeRTL;
    }
    return darkMode ? themeDark : theme;
  };

  const renderLayout = (
    <>
      {layout === "dashboard" && !hideSidenav && (
        <>
          <Sidenav
            color={sidenavColor}
            brandName="VW CMS"
            routes={topLevelNav}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}

      {layout === "vr" && <Configurator />}

      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/authentication" />} />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeToUse()}>
        <CssBaseline />
        {renderLayout}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={themeToUse()}>
      <CssBaseline />
      {renderLayout}
    </ThemeProvider>
  );
}
